"use client";

import React, { Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { legalDocuments } from "@/lib/legal-content";
import { sanitizeHtml } from "@/lib/sanitize";

interface LegalDocumentModalProps {
  isOpen: boolean;
  onClose: () => void;
  documentType: "terms" | "privacy" | "refund" | "cookie" | "studentTerms" | "studentPrivacy";
  title?: string;
}

const LegalDocumentModal: React.FC<LegalDocumentModalProps> = ({
  isOpen,
  onClose,
  documentType,
  title,
}) => {
  const document = legalDocuments[documentType];

  const getDefaultTitle = () => {
    switch (documentType) {
      case "terms":
        return "Terms & Conditions";
      case "privacy":
        return "Privacy Policy";
      case "refund":
        return "Refund Policy";
      case "cookie":
        return "Cookie Policy";
      case "studentTerms":
        return "Student Terms & Conditions";
      case "studentPrivacy":
        return "Student Privacy Policy";
      default:
        return "Legal Document";
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" />
        </Transition.Child>

        {/* Mobile: Bottom Drawer, Desktop: Centered Modal */}
        <div className="fixed inset-0 flex items-end sm:items-center justify-center sm:p-4 zoom-container">
          <Transition.Child
            as={Fragment}
            enter="transform transition ease-in-out duration-500"
            enterFrom="translate-y-full sm:translate-y-0 sm:opacity-0 sm:scale-95"
            enterTo="translate-y-0 sm:opacity-100 sm:scale-100"
            leave="transform transition ease-in-out duration-300"
            leaveFrom="translate-y-0 sm:opacity-100 sm:scale-100"
            leaveTo="translate-y-full sm:translate-y-0 sm:opacity-0 sm:scale-95"
          >
            <Dialog.Panel className="w-full max-w-4xl bg-white rounded-t-3xl sm:rounded-2xl shadow-xl max-h-[95vh] sm:max-h-[90vh] overflow-hidden flex flex-col">
              {/* Drag Handle - Only visible on mobile */}
              <div className="sm:hidden flex justify-center pt-3 pb-2 bg-white rounded-t-3xl">
                <div className="w-12 h-1.5 bg-gray-300 rounded-full" />
              </div>

              {/* Header */}
              <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200 flex-shrink-0">
                <Dialog.Title className="text-lg sm:text-xl font-semibold text-[#463a33]">
                  {title || document?.title || getDefaultTitle()}
                </Dialog.Title>
                <button
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-600 transition-colors p-1"
                >
                  <XMarkIcon className="h-5 w-5 sm:h-6 sm:w-6" />
                </button>
              </div>

              {/* Content */}
              <div className="p-4 sm:p-6 overflow-y-auto flex-1">
                {document ? (
                  <>
                    {/* Document Info */}
                    <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-xs sm:text-sm text-[#463a33]/60 mb-4 sm:mb-6 pb-3 sm:pb-4 border-b border-gray-100">
                      <span>Version: {document.version}</span>
                      <span>•</span>
                      <span>Effective Date: {document.effectiveDate}</span>
                      <span>•</span>
                      <span>Last Updated: {document.lastUpdatedAt}</span>
                    </div>

                    {/* Document Content */}
                    <div className="legal-content text-[#463a33] leading-relaxed prose prose-gray max-w-none prose-sm sm:prose-base">
                      <div
                        dangerouslySetInnerHTML={{
                          __html: sanitizeHtml(document.content.replace(/\n/g, "<br>")),
                        }}
                      />
                    </div>
                  </>
                ) : (
                  <div className="text-center py-8">
                    <p className="text-[#463a33]/60">No document found</p>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="flex justify-center p-4 sm:p-6 border-t border-gray-200 flex-shrink-0">
                <button
                  onClick={onClose}
                  className="w-full sm:w-auto px-6 py-2.5 sm:py-2 bg-[#FF7043] text-white rounded-lg hover:bg-[#FF7043]/90 transition-colors text-sm sm:text-base"
                >
                  Close
                </button>
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
};

export default LegalDocumentModal;
